import { format } from 'date-fns'
/**
 *
 * @param {array of arrays} rawData - array of arrays with the data from the imported excel file
 * @param {integer} headerRow - row number of the header row in excel file (first row is 1)
 * @param {array of objects} columnMap - object should have the following structure { field: '', columnName: '', dataType: '', required: true } where required is a boolean order of fields in the array is important
 * @return {object} - object with the following structure { complete: [],incomplete: [], error: [] }
 */
export default function parseExcelFields(rawData, columnMap, headerRow = 2) {
  let completeRows = [] // array of objects with the data of the rows that have all the required fields
  let invalidRows = [] // array of objects with the data of the rows that have missing required fields
  let incompleteRows = [] // array of objects with the data of the rows that don't have all the required fields
  let dateInvalidRows = [] // array of objects with the data of the rows that have invalid date fields
  let error = [] // array of strings with the errors found in the excel file

  rawData = rawData.filter((row, index) => index < headerRow || row.length > 0) // remove empty rows))
  const data = rawData.slice(parseInt(headerRow) - 1) // remove any rows above the header row

  /**
   * Check if the header row exists if not return an error
   */
  error = checkHeaderRowPosition(rawData, headerRow)
  if (error.length > 0) {
    return {
      complete: [],
      incomplete: [],
      invalid: [],
      dateInvalid: [],
      error: error,
    }
  }
  const rawDataColumns = data[0] // get the header row

  /**
   * Check that the column order on the imported excel file data matches the columnMap
   * If error return object with the following structure { error: [] }
   */
  error = checkErrorsInColumnOrder(columnMap, rawDataColumns)
  error = [...error, ...checkForDataRows(rawData, headerRow)]

  if (error.length > 0) {
    return { error: error }
  }

  const rawDataRows = data.slice(1) // get all rows below the header row

  const requiredColumns = getRequiredColumns(columnMap) // get the required columns

  const requiredFieldsForComplete = getRequiredFieldsForComplete(columnMap) // get the required fields for complete

  /**
   * Parse all rows and check that they have all the required fields
   */
  rawDataRows.forEach((row, index) => {
    let parsedRow = parseRow(row, columnMap)
    let excelRowIndex = index + 1 + headerRow
    let rowErrors = checkRowForErrors(
      parsedRow,
      excelRowIndex,
      columnMap,
      requiredColumns
    )
    if (rowErrors.length > 0) {
      invalidRows.push(rowErrors.map(({ message }) => message).join(', '))
      if (rowErrors.some(({ type }) => type === 'date')) {
        dateInvalidRows.push(excelRowIndex)
      }
    } else {
      if (checkRowComplete(parsedRow, requiredFieldsForComplete)) {
        completeRows.push(parsedRow)
      } else {
        incompleteRows.push(parsedRow)
      }
    }
  })

  if (
    completeRows.length === 0 &&
    incompleteRows.length === 0 &&
    invalidRows.length > 0
  ) {

    let noValidRows = ['No se encontraron datos aptos para importar']
    let noValidDateOnRows = dateInvalidRows.length > 0 ? [`Las fechas no son válidas en ${dateInvalidRows.length === 1 ? 'la fila' : 'las filas:'
      } ${dateInvalidRows.join(', ')}`] : []
    error = [
      ...noValidRows,
      ...invalidRows,
      ...noValidDateOnRows,
      ...error,
    ]
  }

  return {
    complete: completeRows,
    invalid: invalidRows,
    incomplete: incompleteRows,
    dateInvalid: dateInvalidRows,
    error: error,
    total: rawDataRows.length
  }
}

export function getFieldsListIfKey(columnMap, key) {
  return columnMap
    .map((column) => (column?.[key] ? column.field : null))
    .filter((column) => column !== null)
}

/**
 * Iterates columnMap and returns an Array of the required fields indexes and their dataTypes
 * @param {Array} columnMap
 * @returns {Arrar} - Array of objects with the following structure {dataType, index: index of column in the row }
 */
export function getRequiredColumns(columnMap = []) {
  return getFieldsListIfKey(columnMap, 'required')
}

/**
 * Iterates columnMap and returns an Array of the fieldnames of the columns needed to mark the row as complete
 * @param {Array} columnMap
 * @returns {Arrar} - Array of indexes of the columns needed to mark the row as complete
 */
export function getRequiredFieldsForComplete(columnMap = []) {
  return getFieldsListIfKey(columnMap, 'requiredForComplete')
}
/**
 * Check that a given row required columns are not empty
 * @param {array} row - array of strings with the data of the row
 * @param {array of objects} columnMap - object should have the following structure { field: '', columnName: '', dataType: '', required: true } where required is a boolean order of fields in the array is important
 * @returns {(object|string)} - object with a key for each column with the parsed value or string with the error message
 */
export function parseRow(row, columnMap) {
  let parsedRow = {}
  columnMap.forEach((column, index) => {
    parsedRow[column.field] = parseColumn(row[index], column.dataType, column)
  })
  return parsedRow
}

function isStringAndNotEmpty(string) {
  let trimmedString = trimCellContent(string)
  return Boolean(trimmedString) && typeof string === 'string'
    ? trimmedString
    : null
}

/**
 * Apply the correct parse by column datatype
 * @param {*} columnValue
 * @param {*} columnDataType
 * @param {*} columnDefinition
 * @returns
 */
function parseColumn(columnValue, columnDataType, columnDefinition) {
  if (columnValue === undefined) {
    return null
  }
  switch (columnDataType) {
    case 'string':
      return parseString(columnValue, columnDefinition?.maxLength || 100)
    case 'date':
      return parseDate(columnValue)
    case 'email':
      return parseEmail(columnValue)
    case 'number':
      return parseNumber(columnValue)
    case 'integer':
      return parseNumber(columnValue, 'integer')
    case 'gender':
      return parseGender(columnValue)
    case 'schoolCycle':
      return parseSchoolCycle(columnValue)
    default:
      return columnValue
  }
}

/**
 * transform a number including 0 to a string
 * @param {*} number - if not a number return original value
 * @returns {string} - number as a string
 */
function numberToString(number) {
  if (typeof number !== 'number') {
    return number
  }
  if (number === 0) {
    return '0'
  }
  return number.toString()
}

/**
 * Receives a variable, if it is a string and not empty, returns the string, if number it returns a string with the number,
 * for any other type or empty string it returns null'
 */
export function parseSchoolCycle(value) {
  let parsedValue = parseNumber(value, 'integer')
  if (parsedValue === null) {
    return null
  }
  let currentYear = new Date().getFullYear()
  return parsedValue >= 2000 && parsedValue <= currentYear + 20
    ? parsedValue
    : null
}

/**
 * Receives a variable, if it is a string and not empty, returns the string, if number it returns a string with the number,
 * for any other type or empty string it returns null'
 */
export function parseNumber(value, integer = false) {
  if (typeof value === 'string') {
    let numberValue = integer ? parseInt(value) : Number(value)
    return isNaN(numberValue) ? null : numberValue
  }
  return typeof value === 'number' ? value : null
}

/**
 * Receives a variable, if it is a string and not empty, returns the string, if number it returns a string with the number,
 * for any other type or empty string it returns null'
 */
export function parseString(value, maxLength = 100) {
  let isValidString = isStringAndNotEmpty(numberToString(value)) //returns null if string is empty or not a string
  if (isValidString) {
    return truncateString(isValidString.toUpperCase(), maxLength)
  }
  return null
}

/**
 * Receives a variable, if it is a string, it trims the white spaces and checks that is a valid email
 * @param {*} email
 * @returns {string|null} - returns the email if it is valid or null if it is not
 */
export function parseEmail(email, maxLength = 100) {
  let isValidString = isStringAndNotEmpty(email) //returns null if string is empty or not a string
  if (
    isValidString &&
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      isValidString
    ) &&
    isValidString.length <= maxLength
  ) {
    return isValidString.toLowerCase()
  }
  return null
}

/**
 * Receives a variable, if it is a string it trims the white spaces and checks that the remaining length is larger than 0,
 * if it is any other type it returns nulls, if conditions are met, it takes the first character and transform to uppercase,
 * if character matches one of the valid options it returns the character, if not it returns null
 * @param {*} gender
 * @returns {string|null} - returns the email if it is valid or null if it is not
 */
export function parseGender(gender) {
  let isValidString = isStringAndNotEmpty(gender) //returns null if string is empty or not a string
  if (isValidString) {
    let firstLetter = gender[0].toUpperCase()
    return ['M', 'F', 'N'].includes(firstLetter) ? firstLetter : null
  }
  return null
}

/**
 * Receives a variable, if it is a string it trims the white spaces and checks against a regex to check if it matches d-mm-yyyy or dd-MM-yyyy formats,
 * if it is a number it parses the number to a date and returns the date,
 * if it is any other type or the string format is invalid it returns null
 * @param {*} date
 * @param {*} returnIfInvalid
 * @returns {string|null} - returns a string with the date in format MM-dd-yyyy if it is valid or null if it is not
 */
export function parseDate(date, returnIfInvalid = null) {
  if (!Boolean(trimCellContent(date)) && trimCellContent(date) != 0) {
    return null
  }
  try {
    let parsedDate = null
    let fullYear = new Date().getUTCFullYear()
    if (typeof date === 'string') {
      if (
        !/^([1-9]|(0)[1-9]|[1-2]\d|(3)[0-1])(\/|\-)(([1-9]|(0)[1-9])|((1)[0-2]))(\/|\-)\d{4}$/.test(
          date
        )
      ) {
        return returnIfInvalid
      }
      const dateArray = date.includes('-') ? date.split('-') : date.split('/')
      if (parseInt(dateArray[2]) > fullYear) {
        return returnIfInvalid
      }
      parsedDate = format(
        new Date(dateArray[2], dateArray[1] - 1, dateArray[0]),
        'MM-dd-yyyy'
      )
    }
    if (typeof date === 'number') {
      parsedDate = format(ExcelDateToJSDate(date), 'MM-dd-yyyy')
    }
    return parsedDate
  } catch (error) {
    return returnIfInvalid
  }
}

/**
 * Receives an integer, and calculates the date using UTC as reference
 * @param {integer} date - integer from excel date format
 * @returns {string} - returns a string with the date in format MM-dd-yyyy if it is valid or null if it is not
 */
export function ExcelDateToJSDate(serial) {
  let utc_days = Math.floor(serial - 25569)
  let utc_value = utc_days * 86400
  let date_info = new Date(utc_value * 1000)

  return new Date(
    date_info.getUTCFullYear(),
    date_info.getUTCMonth(),
    date_info.getUTCDate(),
    0,
    0,
    0,
    0
  )
}

/**
 * truncate string to specified length
 */
export function truncateString(str, maxLength = 100) {
  if (str.length > maxLength) {
    return str.substring(0, maxLength)
  }
  return str
}

/**
 * Check that a given row required columns are not empty
 * @param {array} row - array of strings with the data of the row
 * @param {array} requiredColumns - array of integers with the index of the required columns
 * @returns {boolean} - true if all the required columns are not empty, false otherwise
 */
export function checkRowForErrors(row, rowIndex, columnMap, requiredColumns) {
  let errors = []
  let fieldDataTypeMap = columnMap.reduce((acc, column) => {
    acc[column.field] = {
      dataType: column?.dataType,
      columnName: column.columnName,
    }
    return acc
  }, {})
  requiredColumns.forEach((field) => {
    if (row[field] === null) {
      errors.push({
        type: 'empty',
        message: `La fila ${rowIndex} tiene campos requeridos vacios`,
      })
      if (fieldDataTypeMap[field].dataType === 'date') {
        errors.push({
          type: 'date',
          message: `La columna ${fieldDataTypeMap[field].columnName} tiene una fecha no válida`,
        })
      }
      if (fieldDataTypeMap[field].dataType === 'gender') {
        errors.push({
          type: 'gender',
          message: `La columna ${fieldDataTypeMap[field].columnName} acepta M, F o N`,
        })
      }
      if (fieldDataTypeMap[field].dataType === 'schoolCycle') {
        errors.push({
          type: 'schoolCycle',
          message: `La columna ${fieldDataTypeMap[field].columnName} debe contener los 4 digitos del año`,
        })
      }
    }
  })

  return errors
}

/**
 * Check that a given row requiredForComplete columns are not empty
 * @param {array} row - array of strings with the data of the row
 * @param {array} requiredColumns - array of integers with the index of the required columns
 * @returns {boolean} - true if all the required columns are not empty, false otherwise
 */
function checkRowComplete(row, requiredFieldForComplete) {
  return !requiredFieldForComplete.some((field) => row[field] === null)
}

// TODO add comments to function
/**
 * Receives a value if string whitespaces are trimmed, if undefined or null return empty string, for any other type return the value
 * @param {*} cellContent
 * @returns {string|original value type} - trimmed string or empty string if undefined or null
 */
export function trimCellContent(cellContent) {
  if (!Boolean(cellContent) && cellContent !== 0) {
    return null
  }
  if (typeof cellContent === 'string') {
    let content = cellContent.trim()
    return content.length > 0 ? content : null
  }
  return cellContent
}

/**
 * Check the position of the header row in the file
 * @author Riccardo Tagliavia
 * @param {array of arrays} rawData - Array of arrays with the data of parsed excel file
 * @param {integer|string} headerRow - integer that indicates the row number of the header or string that indicates the header name
 * @return {array} - empty row position matches
 * @returns
 */
function checkHeaderRowPosition(rawData, headerRow) {
  let errors = []
  if (rawData.length < parseInt(headerRow)) {
    errors.push(
      'El archivo debe tener el encabezado de columnas en la fila ' + headerRow
    )
  }
  return errors
}

/**
 * Check the rows below the header in the file
 * @author Riccardo Tagliavia
 * @param {array of arrays} rawData - Array of arrays with the data of parsed excel file
 * @param {integer|string} headerRow - integer that indicates the row number of the header or string that indicates the header name
 * @return {array} - empty if rows below headerRow are present
 * @returns
 */
function checkForDataRows(rawData, headerRow) {
  let errors = []
  if (rawData.length <= parseInt(headerRow)) {
    errors.push(
      'El archivo no contiene filas con datos debajo del encabezado (filas debajo de la fila ' +
      headerRow +
      ')'
    )
  }
  return errors
}

/**
 * Check that the column order on the imported excel file data matches the columnMap
 * @author Riccardo Tagliavia
 * @param {array of objects} columnMap - object should have the following structure { field: '', columnName: '', dataType: '', required: true } where required is a boolean order of fields in the array is important
 * @param {array} rawDataColumns - array of strings with the column names from the imported excel file
 * @return {array} - empty if the column order is correct, otherwise an array of strings with the errors
 * @returns
 */
function checkErrorsInColumnOrder(columnMap, rawDataColumns) {
  let errors = []
  columnMap.forEach((column, index) => {
    if (
      column.columnName !== rawDataColumns[index]?.trim() ||
      !Boolean(rawDataColumns[index])
    ) {
      errors.push(
        "La columna '" +
        column.columnName +
        "' no se encuentra en el orden correcto, debe estar en la posición " +
        (index + 1)
      )
    }
  })
  return errors
}
